import { api } from './api'
import { miscItemsApi } from './miscItems'

const apiVersion = 'v2'

export const miscItemAttachmentsPath = ({ miscItemId, organisationId, quoteId }) =>
    `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/miscellaneous-items/${miscItemId}/attachments`

// Writes refetch only this item's list. The misc items are left alone: reloading every item
// for one attachment change is what the list endpoint exists to avoid.
const invalidateItemList = (_result, _error, { miscItemId }) => [{ type: 'MiscItemAttachments', id: miscItemId }]

export const miscItemAttachmentsApi = api
    .enhanceEndpoints({
        addTagTypes: ['MiscItemAttachments'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getMiscItemAttachments: builder.query({
                query: miscItemAttachmentsPath,
                providesTags: (_result, _error, { miscItemId }) => [{ type: 'MiscItemAttachments', id: miscItemId }],
                // The misc item row works out its count and failed marker from the attachments that
                // arrive with the misc items. Copy the rows from every list fetch (on open and
                // after a write) so the row stays current without refetching the misc items.
                async onQueryStarted({ miscItemId, organisationId, quoteId }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: rows } = await queryFulfilled
                        dispatch(
                            miscItemsApi.util.updateQueryData('getMiscItems', { organisationId, quoteId }, (draft) => {
                                const item = draft?.find(({ id }) => id === miscItemId)
                                if (!item) return
                                item.attachments = rows
                            })
                        )
                    } catch {
                        // The failed query already reports its own error.
                    }
                },
            }),

            // Fetched at click time: the link is a short-lived SAS URI, so it is never cached.
            getMiscItemAttachmentDownloadUrl: builder.query({
                query: ({ documentId, ...parent }) => `${miscItemAttachmentsPath(parent)}/${documentId}/download-url`,
                keepUnusedDataFor: 0,
            }),

            setMiscItemAttachmentVisibility: builder.mutation({
                query: ({ documentId, isPublic, ...parent }) => ({
                    url: `${miscItemAttachmentsPath(parent)}/${documentId}/visibility`,
                    method: 'PATCH',
                    body: { isPublic },
                }),
                invalidatesTags: invalidateItemList,
            }),

            deleteMiscItemAttachment: builder.mutation({
                query: ({ documentId, ...parent }) => ({
                    url: `${miscItemAttachmentsPath(parent)}/${documentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: invalidateItemList,
            }),
        }),
    })

export const {
    useDeleteMiscItemAttachmentMutation,
    useGetMiscItemAttachmentsQuery,
    useLazyGetMiscItemAttachmentDownloadUrlQuery,
    useSetMiscItemAttachmentVisibilityMutation,
} = miscItemAttachmentsApi
